import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  GripVertical, Trash2, Plus, Save, ArrowLeft, ExternalLink, Loader2,
  Type, Heading1, Image as ImageIcon, Newspaper, Users, ShoppingBag, Calendar,
  Images as ImagesIcon, Minus, ClipboardCheck, X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { WidgetView } from '../components/PageWidgets';
import RichTextEditor from '../components/RichTextEditor';
import ImageField from '../components/ImageField';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';

const MODULES = [
  { type: 'heading', label: 'Heading', icon: Heading1, desc: 'A section title' },
  { type: 'richtext', label: 'Rich text', icon: Type, desc: 'Formatted text block' },
  { type: 'image', label: 'Image', icon: ImageIcon, desc: 'A single picture' },
  { type: 'article', label: 'Article', icon: Newspaper, desc: 'Embed a published article' },
  { type: 'members', label: 'Members', icon: Users, desc: 'Members directory grid' },
  { type: 'shop', label: 'Shop items', icon: ShoppingBag, desc: 'Products from the shop' },
  { type: 'events', label: 'Events', icon: Calendar, desc: 'Upcoming events list' },
  { type: 'gallery', label: 'Photo grid', icon: ImagesIcon, desc: 'Gallery photos (by tag)' },
  { type: 'divider', label: 'Divider', icon: Minus, desc: 'A horizontal rule' },
  { type: 'approvals', label: 'Approval tasks', icon: ClipboardCheck, desc: 'Pending tasks (staff)' },
];
const MODULE_BY_TYPE = Object.fromEntries(MODULES.map((m) => [m.type, m]));
const SPANS = [{ v: 4, label: '⅓' }, { v: 6, label: '½' }, { v: 8, label: '⅔' }, { v: 12, label: 'Full' }];
const uid = () => `w_${Math.random().toString(36).slice(2, 10)}`;

const defaultConfig = (type) => {
  switch (type) {
    case 'heading': return { text: 'New heading', level: 2, span: 12 };
    case 'richtext': return { html: '<p>Write something…</p>', span: 12 };
    case 'image': return { url: '', caption: '', span: 6 };
    case 'article': return { article_id: '', span: 12 };
    case 'members': return { limit: 8, span: 12 };
    case 'shop': return { limit: 6, span: 12 };
    case 'events': return { limit: 5, span: 6 };
    case 'gallery': return { tag: '', limit: 8, span: 12 };
    case 'divider': return { span: 12 };
    case 'approvals': return { span: 12 };
    default: return { span: 12 };
  }
};

function ConfigEditor({ w, patch, articles }) {
  const c = w.config || {};
  const set = (k, v) => patch({ config: { ...c, [k]: v } });
  switch (w.type) {
    case 'heading':
      return (
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={c.text || ''} onChange={(e) => set('text', e.target.value)} placeholder="Heading text" className="flex-1 min-w-[160px]" data-testid="cfg-heading-text" />
          <Select value={String(c.level || 2)} onValueChange={(v) => set('level', Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="2">Large</SelectItem><SelectItem value="3">Small</SelectItem></SelectContent>
          </Select>
        </div>
      );
    case 'richtext':
      return <RichTextEditor value={c.html || ''} onChange={(html) => set('html', html)} />;
    case 'image':
      return (
        <div className="space-y-2">
          <ImageField value={c.url || ''} onChange={(url) => set('url', url)} />
          <Input value={c.caption || ''} onChange={(e) => set('caption', e.target.value)} placeholder="Caption (optional)" />
        </div>
      );
    case 'article':
      return (
        <Select value={c.article_id || ''} onValueChange={(v) => set('article_id', v)}>
          <SelectTrigger className="w-full" data-testid="cfg-article-select"><SelectValue placeholder="Choose an article…" /></SelectTrigger>
          <SelectContent>
            {(articles || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name || 'Untitled'}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case 'gallery':
      return (
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={c.tag || ''} onChange={(e) => set('tag', e.target.value)} placeholder="Filter by tag (blank = all)" className="flex-1 min-w-[140px]" />
          <label className="text-xs" style={{ color: 'var(--olive)' }}>Max</label>
          <Input type="number" min={1} max={40} value={c.limit || 8} onChange={(e) => set('limit', Number(e.target.value))} className="w-20" />
        </div>
      );
    case 'members': case 'shop': case 'events':
      return (
        <div className="flex items-center gap-2">
          <label className="text-xs" style={{ color: 'var(--olive)' }}>How many to show</label>
          <Input type="number" min={1} max={40} value={c.limit || 6} onChange={(e) => set('limit', Number(e.target.value))} className="w-24" data-testid="cfg-limit" />
        </div>
      );
    default:
      return <p className="text-xs" style={{ color: 'var(--olive)' }}>No settings for this module.</p>;
  }
}

export default function PageBuilder() {
  const { pageKey } = useParams();
  const navigate = useNavigate();
  const { customPages, loaded, isAdmin, canAccess, updatePage, fetchPages, listPublicArticles } = useAuth();
  const page = (customPages || []).find((p) => p.page_key === pageKey);
  const isPageAdmin = isAdmin || canAccess('admin-pages');

  const [widgets, setWidgets] = useState(null);
  const [articles, setArticles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragIndex = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const gridRef = useRef(null);
  const draftKey = `pb_draft_${pageKey}`;

  // Load saved widgets once the page is available; restore a newer local draft if present.
  useEffect(() => {
    if (widgets !== null || !page) return;
    const saved = Array.isArray(page.widgets) ? page.widgets : [];
    let initial = saved;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && Array.isArray(draft.widgets) && JSON.stringify(draft.widgets) !== JSON.stringify(saved)) {
          initial = draft.widgets;
          setDirty(true);
          toast('Restored your last unsaved layout.', { description: 'Save to keep it, or reload the page to discard.' });
        }
      }
    } catch (e) { /* ignore */ }
    setWidgets(initial.map((w) => ({ ...w, id: w.id || uid() })));
  }, [page, widgets, draftKey]);

  useEffect(() => { listPublicArticles().then(setArticles).catch(() => setArticles([])); }, [listPublicArticles]);

  // Autosave working draft to localStorage (remembers the last layout across reloads).
  useEffect(() => {
    if (widgets === null || !dirty) return;
    try { localStorage.setItem(draftKey, JSON.stringify({ widgets, at: Date.now() })); } catch (e) { /* ignore */ }
  }, [widgets, dirty, draftKey]);

  const update = (next) => { setWidgets(next); setDirty(true); };
  const addWidget = (type) => update([...(widgets || []), { id: uid(), type, title: '', config: defaultConfig(type) }]);
  const patchWidget = (id, patch) => update(widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)));
  const removeWidget = (id) => update(widgets.filter((w) => w.id !== id));

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...widgets];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update(next);
  };

  // Drag-to-resize: dragging the right edge snaps the column span to the nearest 1/12.
  const resizeRef = useRef(null);
  const onResizeStart = (e, id) => {
    e.preventDefault();
    const grid = gridRef.current;
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    const cell = e.currentTarget.closest('.pw-cell');
    const cellLeft = cell.getBoundingClientRect().left - rect.left;
    resizeRef.current = { id, rect, cellLeft };
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeEnd);
  };
  const onResizeMove = (e) => {
    const r = resizeRef.current;
    if (!r) return;
    const colW = r.rect.width / 12;
    const rel = e.clientX - r.rect.left - r.cellLeft;
    let span = Math.round(rel / colW);
    span = Math.max(2, Math.min(12, span));
    setWidgets((cur) => cur.map((w) => (w.id === r.id ? { ...w, config: { ...w.config, span } } : w)));
    setDirty(true);
  };
  const onResizeEnd = () => {
    resizeRef.current = null;
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
  };

  useEffect(() => () => {
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await updatePage(pageKey, { widgets });
      await fetchPages();
      localStorage.removeItem(draftKey);
      setDirty(false);
      toast.success('Layout saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the layout.');
    } finally { setBusy(false); }
  };

  const livePath = useMemo(() => (page ? (page.path || `/p/${pageKey}`) : '/'), [page, pageKey]);

  if (loaded && !isPageAdmin) {
    return <div className="max-w-lg mx-auto py-24 text-center"><h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Not authorised</h1><p className="mt-2" style={{ color: 'var(--olive)' }}>You need the Pages admin permission to design pages.</p></div>;
  }
  if (!page || widgets === null) {
    return <div className="flex items-center justify-center py-32 gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={18} /> Loading page builder…</div>;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--sage)' }} data-testid="page-builder">
      {/* Top bar */}
      <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5 shadow-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }}>
        <button onClick={() => navigate(`/admin/pages?edit=${pageKey}`)} className="inline-flex items-center gap-1 text-sm hover:opacity-90" data-testid="pb-back"><ArrowLeft size={16} /> Back</button>
        <div className="font-title text-base md:text-lg truncate">Designing: {page.title}</div>
        <div className="ml-auto flex items-center gap-2">
          {dirty && <span className="text-xs opacity-80">Unsaved changes</span>}
          <a href={livePath} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm" style={{ background: 'rgba(255,255,255,0.15)' }} data-testid="pb-view-live"><ExternalLink size={14} /> View live</a>
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-title" style={{ background: 'var(--gold)', color: 'var(--ink)' }} data-testid="pb-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save layout
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Module palette */}
        <aside className="lg:w-64 flex-shrink-0 p-3 lg:h-[calc(100vh-49px)] lg:sticky lg:top-[49px] lg:overflow-auto" style={{ background: 'rgba(255,255,255,0.55)' }}>
          <h2 className="font-title text-sm uppercase tracking-wide mb-2 px-1" style={{ color: 'var(--olive)' }}>Add a module</h2>
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
            {MODULES.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.type} onClick={() => addWidget(m.type)} data-testid={`pb-add-${m.type}`}
                  className="flex items-center gap-2 text-left px-3 py-2 rounded-lg bg-white hover:shadow transition-shadow" style={{ border: '1px solid rgba(0,0,0,0.08)' }}>
                  <Icon size={16} style={{ color: 'var(--maroon)' }} />
                  <span className="min-w-0">
                    <span className="block text-sm font-title leading-tight" style={{ color: 'var(--navy)' }}>{m.label}</span>
                    <span className="block text-[11px] leading-tight" style={{ color: 'var(--olive)' }}>{m.desc}</span>
                  </span>
                  <Plus size={14} className="ml-auto flex-shrink-0" style={{ color: 'var(--olive)' }} />
                </button>
              );
            })}
          </div>
        </aside>

        {/* Canvas */}
        <main className="flex-1 p-4 md:p-6 pb-canvas">
          {widgets.length === 0 ? (
            <div className="max-w-md mx-auto text-center py-24 rounded-lg" style={{ border: '2px dashed rgba(0,0,0,0.15)' }} data-testid="pb-empty">
              <p className="font-title text-lg" style={{ color: 'var(--navy)' }}>Your page is empty</p>
              <p className="text-sm mt-1" style={{ color: 'var(--olive)' }}>Pick a module on the left to start building. Drag the header to reorder, and drag the right edge to resize.</p>
            </div>
          ) : (
            <div ref={gridRef} className="pw-grid">
              {widgets.map((w, i) => {
                const mod = MODULE_BY_TYPE[w.type] || {};
                const Icon = mod.icon || Type;
                const span = Number(w.config?.span) || 12;
                return (
                  <div key={w.id} className="pw-cell" style={{ '--pw-span': span }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(i); }}
                    onDrop={(e) => { e.preventDefault(); reorder(dragIndex.current, i); dragIndex.current = null; setDragOver(null); }}>
                    <div className={`pb-window ${dragOver === i ? 'dragover' : ''}`} data-testid={`pb-window-${w.type}`}>
                      {/* window header */}
                      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}
                        draggable
                        onDragStart={(e) => { dragIndex.current = i; e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(i)); } catch (err) { /* ignore */ } }}
                        onDragEnd={() => { dragIndex.current = null; setDragOver(null); }}>
                        <GripVertical size={15} className="pb-handle" style={{ color: 'var(--olive)' }} data-testid={`pb-drag-${w.id}`} />
                        <Icon size={14} style={{ color: 'var(--maroon)' }} />
                        <input value={w.title || ''} onChange={(e) => patchWidget(w.id, { title: e.target.value })} placeholder={mod.label || w.type}
                          className="bg-transparent text-sm font-title outline-none flex-1 min-w-0" style={{ color: 'var(--navy)' }} data-testid={`pb-title-${w.id}`} />
                        <div className="flex items-center gap-0.5 mr-1">
                          {(() => { const near = SPANS.reduce((a, b) => (Math.abs(b.v - span) < Math.abs(a.v - span) ? b : a)); return SPANS.map((s) => (
                            <button key={s.v} onClick={() => patchWidget(w.id, { config: { ...w.config, span: s.v } })}
                              title={`Width: ${s.label}`} data-testid={`pb-span-${w.id}-${s.v}`}
                              className="w-6 h-6 rounded text-[11px] font-title"
                              style={near.v === s.v ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.12)' }}>
                              {s.label}
                            </button>
                          )); })()}
                        </div>
                        <button onClick={() => removeWidget(w.id)} title="Remove" data-testid={`pb-remove-${w.id}`} className="p-1 rounded hover:bg-black/5" style={{ color: 'var(--maroon)' }}><Trash2 size={14} /></button>
                      </div>
                      {/* config */}
                      <div className="p-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                        <ConfigEditor w={w} patch={(patch) => patchWidget(w.id, patch)} articles={articles} />
                      </div>
                      {/* live preview */}
                      <div className="p-3">
                        <div className="text-[11px] uppercase tracking-wide mb-2" style={{ color: 'var(--olive)' }}>Preview</div>
                        <WidgetView w={w} />
                      </div>
                    </div>
                    {/* drag-to-resize handle (desktop) */}
                    <div onPointerDown={(e) => onResizeStart(e, w.id)} data-testid={`pb-resize-${w.id}`}
                      className="hidden md:block absolute top-0 right-[-6px] w-3 h-full cursor-ew-resize"
                      style={{ touchAction: 'none' }} title="Drag to resize">
                      <div className="w-1 h-10 rounded-full mx-auto mt-4" style={{ background: 'var(--gold)', opacity: 0.7 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
